@if (this.isLoading$ | async) {
    <div class="spinner-container mb-3 flex-row justify-center items-center">
        <mat-progress-spinner color="accent" mode="indeterminate" diameter="30" class="self-center">
        </mat-progress-spinner>
    </div>
}
@else if (this.data) {
    <mat-card data-qa-id="job-requirements-qualifications" *ngIf="showJobRequirementsAndQualifications && data.jobClinicalTrackRequirements; let req" class="job-unit-requirements" [ngClass]="useClass">
        @if (shouldDisplayComparison) {
            <div class="job-requirements-changes-note">
                <div class="job-requirements-changes-note__icon">
                    <mat-icon>info</mat-icon>
                </div>
                <div class="job-requirements-changes-note__content" data-qa-id="an_rjd_requirements-changes-note">
                    @if (this.data.matchType === matchType.UnitRequirements || this.data.matchType === matchType.UnitTrack) {
                        Job requirements no longer align with the UD. This affects the match breakdown and how the job is displayed in Aya Talent Marketplace. The current tab contains the most accurate job requirements. Use the toggle to compare with UD.
                    }
                    @else if (this.data.matchType === matchType.JobTrack) {
                        Clinical Track is in use and job requirements have been edited. Job requirements no longer align with the Clinical Track. This affects the match breakdown and how the job is displayed in Aya Talent Marketplace. The edited version tab contains the most accurate job requirements. Use the toggle to compare with the Clinical Track.
                    }
                </div>
            </div>

            <div class="gap-8 flex-col section-header-spacer">
                <div class="flex-row-wrap space-between items-center gap-8">
                    <h1>Job Requirements & Qualifications</h1>
                    <mat-button-toggle-group class="comparison-toggle-group mat-button-toggle-group-small" [value]="(compareSelection$ | async)" (change)="onChangesToggled($event)" data-qa-id="ayaChangesTypeToggler">
                        <mat-button-toggle [value]="currentToggleText" data-qa-id="ayaChangesTypeCurrentToggle">Current</mat-button-toggle>
                        <mat-button-toggle [value]="compareToggleText" data-qa-id="ayaChangesTypeCompareToggle">{{comparisonText}}</mat-button-toggle>
                    </mat-button-toggle-group>
                </div>

                <div class="job-requirements-updated-date justify-end" data-qa-id="job-requirements-updated-date">
                    Edits made on {{data.jobRequirementsUpdatedDate | lenientDate:'M/d/yy h:mm a':true}}
                </div>
            </div>

            <ng-container *ngIf="compareSelection$ | async as compareSelection">
                @if (compareSelection === currentToggleText) {
                    <ng-container *ngTemplateOutlet="requirements; context: { $implicit: req }"></ng-container>
                }
                @else if (compareSelection === compareToggleText) {
                    <nova-job-qualifications-compare [jobId]="jobId" [shouldShowStateLicenseField]="shouldShowStateLicenseField"></nova-job-qualifications-compare>
                }
            </ng-container>
        }
        @else {
            <div class="section-header-spacer">
                <h1>Job Requirements & Qualifications</h1>
            </div>

            <ng-container *ngTemplateOutlet="requirements; context: { $implicit: req }"></ng-container>
        }
    </mat-card>

    <div class="wrap-divider">
        <hr>
    </div>

    <ng-template #requirements let-req>
        <div class="flex-col gap-16">
            @if (shouldShowPreviousChargeExperience) {
                <div class="flex-row">
                    <strong class="flex-40 nova__label">Previous Charge Experience:</strong>
                    <span class="flex-60 capitalize" data-qa-id="an_rjd_previousChargeExperience_readonly">
                        {{ req.previousChargeExperience | translateExperience | nullDash}}
                    </span>
                </div>
            }

            <div class="flex-row">
                <strong class="flex-40 nova__label">Years of Experience:</strong>
                <div class="flex-60" data-qa-id="an_rjd_yearOfExperience_readonly">
                    {{req.yearOfExperience | nullDash}}
                    @if (req.yearOfExperience !== null) {
                        <span> year(s)</span>
                    }
                    <span *ngIf="req.minimumExperienceRequiredOther">
                        <br/> Note: {{req.minimumExperienceRequiredOther}}
                    </span>
                </div>
            </div>

            <div class="flex-row">
                <strong class="flex-40 nova__label">Patient Ratio Experience:</strong>
                <span class="flex-60" data-qa-id="an_rjd_patientRatioExperience_readonly">
                    {{req.patientRatioExperience | staffingRatio | nullDash}}
                  </span>
            </div>

            <div class="flex-row">
                <strong class="flex-40 nova__label">Charting System:</strong>
                <!-- Make sure this stays in line or else it adds an extra space like "Required , Any" -->
                <span class="flex-60 capitalize" data-qa-id="an_rjd_chartingSystemName_readonly">{{req.chartingSystemExperience | translateExperience | nullDash}}<span *ngIf="(req.chartingSystemExperience | translateExperience | nullDash) !== '-'">, {{(req.chartingSystemName | nullDash) === '-' ? 'Any' : req.chartingSystemName}}</span></span>
            </div>

            <div class="flex-row">
                <strong class="flex-40 nova__label">Teaching Hospital Experience:</strong>
                <span class="flex-60 capitalize" data-qa-id="an_rjd_teachingHospitalExperience_readonly">
                    {{req.teachingHospitalExperience | translateExperience | nullDash}}
                </span>
            </div>

            <div class="flex-row">
                <strong class="flex-40 nova__label">Community Hospital Experience:</strong>
                <span class="flex-60 capitalize" data-qa-id="an_rjd_communityHospitalExperience_readonly">
                    {{req.communityHospitalExperience | translateExperience | nullDash}}
                </span>
            </div>

            <div class="flex-row">
                <strong class="flex-40 nova__label">LTAC Experience:</strong>
                <span class="flex-60 capitalize" data-qa-id="an_rjd_ltacExperience_readonly">
                    {{ req.ltacExperience | translateExperience | nullDash}}
                </span>
            </div>

            <div class="flex-row">
                <strong class="flex-40 nova__label">Trauma Level I Experience:</strong>
                <span class="flex-60 capitalize" data-qa-id="an_rjd_traumaLevelIExperience_readonly">
                    {{req.traumaLevelIExperience | translateExperience | nullDash}}
                </span>
            </div>

            <div class="flex-row">
                <strong class="flex-40 nova__label">Trauma Level II Experience:</strong>
                <span class="flex-60 capitalize" data-qa-id="an_rjd_traumaLevelIIExperience_readonly">
                    {{req.traumaLevelIIExperience | translateExperience | nullDash}}
                </span>
            </div>

            <div class="flex-row">
                <strong class="flex-40 nova__label">Travel Experience Required:</strong>
                <div class="flex-60" data-qa-id="an_rjd_travelExperienceRequired_readonly">
                    <span class="capitalize">{{req.travelExperience | translateExperience | nullDash}}</span>
                    <span *ngIf="req.unitAcceptsFirstTimeTravelerOther">
                        <br/>
                        Note: {{req.unitAcceptsFirstTimeTravelerOther}}
                    </span>
                </div>
            </div>

            @if (shouldShowStateLicenseField) {
                <div class="flex-row">
                    <strong class="flex-40 nova__label">State License:</strong>
                    <span class="flex-60 capitalize" data-qa-id="an_rjd_stateLicense_readonly">
                        {{ req.stateLicense | translateExperience | nullDash }}
                    </span>
                </div>
            }

            <div class="flex-row">
                <div class="flex-40">
                    <strong class="nova__label">Certifications:</strong>
                    <br />
                    <span class="note">Certs marked with * are preferred</span>
                </div>
                <span class="flex-60" data-qa-id="an_rjd_certifications_readonly">
                    @for (cert of req.certifications; track cert.id; let isLast = $last) {
                        @if (!cert.isNA) {
                            <span>{{ cert.name }}@if (cert.isPreferred) {
*
}@if (isAlternativeCertificationsEnabled()) {
                                @if (getAlternativesForCertification(cert.id); as alternatives) {
                                    @if (alternatives.length > 0) {
                                        <span class="alternatives-text"> (alts: {{ alternatives.join(', ') }})</span>
                                    }
                                }
                            }@if (!isLast) {
,
}</span>
                        }
                    }
                    @if (req.certifications?.length === 0 || !req.certifications) {
                        <span>-</span>
                    }
                </span>
            </div>

            <div class="flex-row">
                <div class="flex-40">
                    <strong class="nova__label">Skills:</strong>
                    <br />
                    <span class="note">Skills marked with * are preferred</span>
                </div>

                <div class="flex-60" data-qa-id="an_rjd_skills_readonly">
                    <ng-container *ngIf="req.skills && req.skills.length > 0; else noSkills">
                        <div *ngFor="let category of getUniqueSkillsCategories(req.skills); let isFirst = first"
                            [ngClass]="{'first-skill-set': isFirst}">
                            <p class="skill-category" data-qa-id="an_rjd_skillCategory_readonly">
                                {{ category }}
                            </p>
                            <div *ngFor="let skill of getSkillsByCategory(req.skills, category)">
                                <span data-qa-id="an_rjd_skillName_readonly">
                                    {{ skill.name }}<span *ngIf="skill.isPreferred">*</span>
                                </span>
                            </div>
                        </div>
                    </ng-container>
                    <ng-template #noSkills>-</ng-template>
                </div>
            </div>
        </div>
    </ng-template>

    <mat-card data-qa-id="unit-details" *ngIf="showUnitDetails && data.unitRequirements; let req" class="job-unit-requirements" [ngClass]="useClass">
        <div class="section-header-spacer">
            <h1>Unit Details</h1>
        </div>

        <div class="flex-col gap-24">
            <div class="flex-col gap-16">
                <strong>Staffing & Scheduling</strong>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Scheduling Type:</strong>
                    <span class="flex-60" data-qa-id="an_rjd_schedulingType_readonly">
                        {{req.schedulingType | nullDash | titlecase }}
                    </span>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Patient Ratios: </strong>
                    <div class="flex-60" data-qa-id="an_rjd_patientRatioExperience_readonly">
                        <span>Days: {{req.staffingRatioDays | staffingRatio | nullDash }}</span><br />
                        <span>Nights: {{req.staffingRatioNights | staffingRatio | nullDash }}</span><br />
                        <span>Weekends: {{req.staffingRatioWeekends | staffingRatio | nullDash }}</span>
                    </div>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Float Required:</strong>
                    <div class="flex-60" data-qa-id="an_rjd_floatRequired_readonly">
                        <span *ngIf="req.floatRequired !== null; else noFloatRequired">
                            Yes
                            <br />
                            <span *ngIf="req.floatRequired">Note: </span>{{ req.floatRequired }}
                        </span>
                        <ng-template #noFloatRequired>-</ng-template>
                    </div>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Call Required:</strong>
                    <div class="flex-60" data-qa-id="an_rjd_callRequired_readonly">
                        <span *ngIf="req.callRequired !== null; else noCallRequired">
                            Yes
                            <br />
                            <span *ngIf="req.callRequired">Note: </span>{{ req.callRequired }}
                        </span>
                        <ng-template #noCallRequired>-</ng-template>
                    </div>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Weekend Coverage:</strong>
                    <div class="flex-60" data-qa-id="an_rjd_weekendCoverage_readonly">
                        <div *ngIf="req.weekendCoverageEveryOther">Every Other</div>
                        <div *ngIf="req.weekendCoverageEveryThird">Every Third</div>
                        <div *ngIf="!req.weekendCoverageEveryThird && !req.weekendCoverageEveryOther">-</div>
                    </div>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Number of Weekend Shifts Per Contract:</strong>
                    <span class="flex-60" data-qa-id="an_rjd_weekendShiftPerContract_readonly">
                        {{req.weekendShiftPerContract | nullDash }}
                    </span>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Pre-Approved Time Off:</strong>
                    <div class="flex-60" data-qa-id="an_rjd_preApprovedTimeOffPerContract_readonly">
                        <ng-container *ngIf="req.preApprovedTimeOffPerContract; let toff; else elseBlock">
                            <span *ngIf="toff === 'one'">1 Request</span>
                            <span *ngIf="toff === 'two'">2 Requests</span>
                            <span *ngIf="toff === 'none'">None</span>
                        </ng-container>
                        <ng-template #elseBlock>
                            <span>-</span>
                        </ng-template>
                    </div>
                </div>

              <div class="flex-row">
                  <strong class="flex-40 nova__label">Orientation Hours:</strong>
                  <span class="flex-60" data-qa-id="an_rjd_orientationHours_readonly">
                      {{req.orientationHours | nullDash }}
                  </span>
              </div>
            </div>

            <div class="flex-col gap-16">
                <strong>Facility & Patient Care Details</strong>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Patient Age Groups:</strong>
                    <span class="flex-60" data-qa-id="an_rjd_patientAgeGroup_readonly">
                        {{req.patientAgeGroups | joinComma}}
                    </span>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Daily Census:</strong>
                    <span class="flex-60" data-qa-id="an_rjd_averageDailyCensus_readonly">
                        {{req.averageDailyCensus | nullDash}}
                    </span>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Number of Visits Per Day:</strong>
                    <span class="flex-60" data-qa-id="an_rjd_visitsDays_readonly">
                        {{req.visitsDays | nullDash}}
                    </span>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Number of Rooms:</strong>
                    <span class="flex-60" data-qa-id="an_rjd_rooms_readonly">
                        {{req.rooms | nullDash}}
                    </span>
                </div>

              <div class="flex-row">
                  <strong class="flex-40 nova__label">Number of Beds:</strong>
                  <span class="flex-60" data-qa-id="an_rjd_beds_readonly">
                      {{req.beds | nullDash}}
                  </span>
              </div>
            </div>

            <div class="flex-col gap-16">
                <strong>Additional Unit Information</strong>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Interdisciplinary Support:</strong>
                    <span class="flex-60" data-qa-id="an_rjd_interdisciplinarySupport_readonly">
                        {{req.providerSupports | joinComma}}
                    </span>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Patient Diagnoses:</strong>
                    <span class="flex-60"
                        data-qa-id="an_rjd_patientPopulation_readonly"
                        [innerHtml]="req.patientPopulation | textFormat | nullDash">
                    </span>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Special Procedures/Unit Details:</strong>
                    <span class="flex-60"
                        data-qa-id="an_rjd_unitDetails_readonly"
                        [innerHtml]="req.unitDetails | textFormat | nullDash">
                    </span>
                </div>

                <div class="flex-row">
                    <strong class="flex-40 nova__label">Special Equipment:</strong>
                    <span class="flex-60"
                        data-qa-id="an_rjd_specialEquipment_readonly"
                        [innerHtml]="req.specialEquipment | textFormat | nullDash">
                    </span>
                </div>
            </div>
        </div>
    </mat-card>
} @else {
    <mat-card data-qa-id="job-requirements-qualifications" class="job-unit-requirements" [ngClass]="useClass">
        <div class="section-header-spacer">
            <h1>Job Requirements & Qualifications</h1>
        </div>
    </mat-card>
}
